<template>
  <AppModal :open="true" :title="$t('reviews.dialogTitle')" @close="$emit('close')">
    <p class="text-[13px] text-muted-foreground truncate">{{ routeName }}</p>
    <div class="mt-3 flex justify-center">
      <StarRating v-model="stars" interactive :size="36" :label="$t('reviews.yourRating')" />
    </div>
    <label class="mt-3 flex flex-col gap-1">
      <span class="text-[12px] font-semibold text-muted-foreground">{{ $t('reviews.commentLabel') }}</span>
      <textarea
        v-model="comment"
        rows="4"
        maxlength="1000"
        :placeholder="$t('reviews.commentPlaceholder')"
        class="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-[14px] leading-snug focus:outline-none focus:border-ring"
      />
    </label>
    <p v-if="error" class="mt-2 text-[12.5px] text-destructive">{{ error }}</p>
    <div class="mt-4 flex items-center gap-2">
      <button v-if="existing" type="button" class="btn btn--danger" :disabled="busy" @click="remove">{{ $t('reviews.delete') }}</button>
      <button type="button" class="btn btn--primary ml-auto" :disabled="!stars || busy" @click="save">{{ $t('reviews.submit') }}</button>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "../../api";
import AppModal from "../ui/AppModal.vue";
import StarRating from "./StarRating.vue";
import type { ReviewsResponse } from "../../types/reviews";

const props = defineProps<{ routeId: number; routeName: string; existing: { stars: number; comment: string | null } | null }>();
const emit = defineEmits<{ (e: "close"): void; (e: "saved", data: ReviewsResponse): void }>();
const { t } = useI18n();

const stars = ref<number | null>(props.existing?.stars ?? null);
const comment = ref(props.existing?.comment ?? "");
const busy = ref(false);
const error = ref("");

const run = async (request: () => Promise<{ data: ReviewsResponse }>) => {
  busy.value = true;
  error.value = "";
  try {
    emit("saved", (await request()).data);
    emit("close");
  } catch {
    error.value = t("reviews.error");
  } finally {
    busy.value = false;
  }
};

const save = () => run(() => api.put(`/routes/${props.routeId}/review`, { stars: stars.value, comment: comment.value.trim() || null }));
const remove = () => run(() => api.delete(`/routes/${props.routeId}/review`));
</script>
